/**
 * Contact/Support page for Vibe Commenting
 */

import React from 'react';
import { Link } from 'react-router-dom';
import AuroraBackground from '../components/AuroraBackground';
import SEO from '../components/SEO';

const ContactPage: React.FC = () => {
    const email = 'yuyang.jiang@atelier-persopol.com';
    const subject = 'Support Request - Vibe Commenting';
    const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}`;

    return (
        <>
            <SEO
                title="Contact Us | Vibe Commenting"
                description="Get in touch with the Vibe Commenting team. Questions about AI Instagram commenting, account setup, or pricing? We're here to help."
                path="/contact"
            />
            <AuroraBackground />
            <div style={styles.container}>
                <div style={styles.card}>
                    {/* Header */}
                    <div style={styles.header}>
                        <h1 style={styles.title}>Contact Us</h1>
                        <p style={styles.subtitle}>We're here to help</p>
                    </div>

                    {/* Content */}
                    <div style={styles.content}>
                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>Get in Touch</h2>
                            <p style={styles.paragraph}>
                                Have questions, feedback, or need assistance with Vibe Commenting?
                                We'd love to hear from you. Our team is committed to providing
                                excellent support and typically responds within 24-48 hours.
                            </p>
                        </section>

                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>Email Support</h2>
                            <p style={styles.paragraph}>
                                For any inquiries, please reach out to us at:
                            </p>
                            <div style={styles.contactBox}>
                                <a href={mailtoLink} style={styles.emailLink}>
                                    {email}
                                </a>
                            </div>
                            <p style={styles.hint}>
                                Click the email address above to open your mail client with
                                a pre-filled subject line.
                            </p>
                        </section>

                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>What to Include</h2>
                            <p style={styles.paragraph}>
                                To help us assist you faster, please include:
                            </p>
                            <ul style={styles.list}>
                                <li>A clear description of your question or issue</li>
                                <li>Your account email (if applicable)</li>
                                <li>Any relevant screenshots or error messages</li>
                                <li>Steps to reproduce the issue (for bug reports)</li>
                            </ul>
                        </section>
                    </div>

                    {/* Footer Links */}
                    <div style={styles.footer}>
                        <Link to="/privacy" style={styles.footerLink}>
                            Privacy Policy
                        </Link>
                        <span style={styles.separator}>|</span>
                        <Link to="/" style={styles.button}>
                            Back to Home
                        </Link>
                    </div>
                </div>
            </div>
        </>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    container: {
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '100px 20px 40px',
    },
    card: {
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderRadius: '16px',
        padding: '48px',
        maxWidth: '600px',
        width: '100%',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    },
    header: {
        textAlign: 'center',
        marginBottom: '32px',
        paddingBottom: '24px',
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
    },
    title: {
        fontSize: '36px',
        fontWeight: 700,
        color: '#000000',
        marginBottom: '8px',
    },
    subtitle: {
        fontSize: '16px',
        color: '#666666',
    },
    content: {
        color: '#333333',
    },
    section: {
        marginBottom: '28px',
    },
    sectionTitle: {
        fontSize: '20px',
        fontWeight: 600,
        color: '#000000',
        marginBottom: '12px',
    },
    paragraph: {
        fontSize: '15px',
        lineHeight: 1.7,
        marginBottom: '12px',
        color: '#444444',
    },
    contactBox: {
        background: '#F7F7F7',
        borderRadius: '8px',
        padding: '20px',
        textAlign: 'center',
        marginTop: '16px',
        marginBottom: '12px',
    },
    emailLink: {
        color: '#7C3AED',
        textDecoration: 'none',
        fontSize: '18px',
        fontWeight: 600,
    },
    hint: {
        fontSize: '13px',
        color: '#888888',
        textAlign: 'center',
        fontStyle: 'italic',
    },
    list: {
        paddingLeft: '24px',
        marginBottom: '16px',
        lineHeight: 1.8,
        color: '#444444',
        fontSize: '15px',
    },
    footer: {
        marginTop: '32px',
        paddingTop: '24px',
        borderTop: '1px solid rgba(0, 0, 0, 0.1)',
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        flexWrap: 'wrap',
    },
    footerLink: {
        color: '#7C3AED',
        textDecoration: 'none',
        fontSize: '14px',
    },
    separator: {
        color: '#CCCCCC',
    },
    button: {
        display: 'inline-block',
        background: '#7C3AED',
        color: '#FFFFFF',
        border: 'none',
        borderRadius: '8px',
        padding: '14px 28px',
        fontSize: '15px',
        fontWeight: 600,
        cursor: 'pointer',
        textDecoration: 'none',
        transition: 'all 0.2s ease',
    },
};

export default ContactPage;
